import { getAppVisualZoomScale } from './fullCalendarZoom';

const observedTabsRoots = new Set();
const resizeObservers = new Map();
const scrollerGuards = new WeakMap();
const guardedScrollers = new Set();

const SCROLL_BUTTON_SELECTOR = '.MuiTabScrollButton-root, .MuiTabs-scrollButtons';

const isTabInDialogPortal = (tabsRoot) =>
  Boolean(tabsRoot?.closest('.MuiDialog-root, .MuiModal-root'));

const isTabInZoomedAppRoot = (tabsRoot) => {
  if (!tabsRoot || getAppVisualZoomScale() >= 1) return false;
  const root = document.getElementById('root');
  return Boolean(root?.contains(tabsRoot));
};

const isScrollableTabsRoot = (tabsRoot) =>
  Boolean(tabsRoot?.querySelector('.MuiTabs-scrollableX, .MuiTabs-scrollableY'));

// Scrollable tabs in #root use CSS/JS counter-zoom; MUI handles indicator/scroll natively.
const shouldSkipTabsZoomFix = (tabsRoot) =>
  !isTabInDialogPortal(tabsRoot)
  && isTabInZoomedAppRoot(tabsRoot)
  && isScrollableTabsRoot(tabsRoot);

const getNativeTabOffset = (selectedTab) => ({
  left: selectedTab.offsetLeft,
  width: selectedTab.offsetWidth,
  top: selectedTab.offsetTop,
  height: selectedTab.offsetHeight,
});

const getTabOffsetInFlexContainer = (selectedTab, flexContainer, tabsRoot) => {
  if (!selectedTab || !flexContainer) return null;

  if (isTabInDialogPortal(tabsRoot) || !isTabInZoomedAppRoot(tabsRoot)) {
    if (selectedTab.offsetParent === flexContainer) {
      return getNativeTabOffset(selectedTab);
    }

    const tabRect = selectedTab.getBoundingClientRect();
    const flexRect = flexContainer.getBoundingClientRect();

    return {
      left: tabRect.left - flexRect.left,
      width: tabRect.width,
      top: tabRect.top - flexRect.top,
      height: tabRect.height,
    };
  }

  const zoomScale = getAppVisualZoomScale();
  const tabRect = selectedTab.getBoundingClientRect();
  const flexRect = flexContainer.getBoundingClientRect();
  const scale = zoomScale > 0 ? zoomScale : 1;

  return {
    left: (tabRect.left - flexRect.left) / scale,
    width: tabRect.width / scale,
    top: (tabRect.top - flexRect.top) / scale,
    height: tabRect.height / scale,
  };
};

const fixTabsIndicator = (tabsRoot) => {
  if (shouldSkipTabsZoomFix(tabsRoot)) return false;

  const indicator = tabsRoot.querySelector('.MuiTabs-indicator');
  const selectedTab = tabsRoot.querySelector('.MuiTab-root.Mui-selected');
  const flexContainer = tabsRoot.querySelector('.MuiTabs-flexContainer');

  if (!indicator || !selectedTab || !flexContainer) return false;

  const metrics = getTabOffsetInFlexContainer(selectedTab, flexContainer, tabsRoot);
  if (!metrics || metrics.width <= 0) return false;

  const isVertical = tabsRoot.getAttribute('aria-orientation') === 'vertical'
    || tabsRoot.classList.contains('MuiTabs-vertical');

  if (isVertical) {
    indicator.style.top = `${metrics.top}px`;
    indicator.style.height = `${metrics.height}px`;
    return true;
  }

  indicator.style.left = `${metrics.left}px`;
  indicator.style.width = `${metrics.width}px`;
  return true;
};

const applyScrollableTabsCounterZoom = (tabsRoot) => {
  const scale = getAppVisualZoomScale();

  if (!shouldSkipTabsZoomFix(tabsRoot)) {
    tabsRoot.style.removeProperty('zoom');
    tabsRoot.style.removeProperty('transform-origin');
    tabsRoot.style.removeProperty('width');
    tabsRoot.style.removeProperty('max-width');
    return;
  }

  tabsRoot.style.zoom = `${(1 / scale) * 100}%`;
  tabsRoot.style.transformOrigin = 'top left';
  tabsRoot.style.width = '100%';
  tabsRoot.style.maxWidth = '100%';
};

const installScrollerGuard = (scroller, tabsRoot) => {
  if (!scroller || guardedScrollers.has(scroller) || !shouldSkipTabsZoomFix(tabsRoot)) return;

  guardedScrollers.add(scroller);
  const guard = {
    active: false,
    intentDirection: 0,
    maxScrollLeft: scroller.scrollLeft,
    minScrollLeft: scroller.scrollLeft,
    rafId: null,
  };
  scrollerGuards.set(scroller, guard);

  const loop = () => {
    if (guard.active && guard.intentDirection > 0 && scroller.scrollLeft < guard.maxScrollLeft - 1) {
      scroller.scrollLeft = guard.maxScrollLeft;
    } else if (guard.active && guard.intentDirection < 0 && scroller.scrollLeft > guard.minScrollLeft + 1) {
      scroller.scrollLeft = guard.minScrollLeft;
    }
    guard.rafId = requestAnimationFrame(loop);
  };
  loop();
};

const getScrollButtonIntentDirection = (scrollButton, tabsRoot) => {
  const buttons = tabsRoot.querySelectorAll(SCROLL_BUTTON_SELECTOR);
  if (!buttons.length) return 0;

  const isStartButton = buttons[0] === scrollButton;
  const isRtl = getComputedStyle(tabsRoot).direction === 'rtl';
  if (isStartButton) return isRtl ? 1 : -1;
  return isRtl ? -1 : 1;
};

const activateScrollerExplore = (scroller, intentDirection) => {
  const guard = scrollerGuards.get(scroller);
  if (!guard || !intentDirection) return;

  guard.active = true;
  guard.intentDirection = intentDirection;
  guard.maxScrollLeft = scroller.scrollLeft;
  guard.minScrollLeft = scroller.scrollLeft;
};

const updateScrollerExplorePosition = (scroller) => {
  const guard = scrollerGuards.get(scroller);
  if (!guard?.active || !guard.intentDirection) return;

  guard.maxScrollLeft = Math.max(guard.maxScrollLeft, scroller.scrollLeft);
  guard.minScrollLeft = Math.min(guard.minScrollLeft, scroller.scrollLeft);
};

const deactivateScrollerExplore = (scroller) => {
  const guard = scrollerGuards.get(scroller);
  if (!guard) return;

  guard.active = false;
  guard.intentDirection = 0;
};

const syncScrollableTabsZoomLayout = () => {
  document.querySelectorAll('#root .MuiTabs-root').forEach((tabsRoot) => {
    if (!isScrollableTabsRoot(tabsRoot)) return;

    applyScrollableTabsCounterZoom(tabsRoot);

    const scroller = tabsRoot.querySelector('.MuiTabs-scroller');
    if (scroller) installScrollerGuard(scroller, tabsRoot);
  });
};

export const fixTabsIndicatorsForAppZoom = () => {
  syncScrollableTabsZoomLayout();

  document.querySelectorAll('.MuiTabs-root').forEach((tabsRoot) => {
    if (shouldSkipTabsZoomFix(tabsRoot)) return;
    if (getAppVisualZoomScale() >= 1 && !isTabInDialogPortal(tabsRoot)) return;
    fixTabsIndicator(tabsRoot);
    observeTabsRoot(tabsRoot);
  });
};

const observeTabsRoot = (tabsRoot) => {
  if (shouldSkipTabsZoomFix(tabsRoot)) return;
  if (observedTabsRoots.has(tabsRoot)) return;
  observedTabsRoots.add(tabsRoot);

  const flexContainer = tabsRoot.querySelector('.MuiTabs-flexContainer');
  if (!flexContainer || typeof ResizeObserver === 'undefined') return;

  const resizeObserver = new ResizeObserver(() => {
    scheduleTabsIndicatorFix();
  });

  resizeObserver.observe(flexContainer);
  flexContainer.querySelectorAll('.MuiTab-root').forEach((tab) => {
    resizeObserver.observe(tab);
  });
  resizeObservers.set(tabsRoot, resizeObserver);
};

let frameId = null;
let burstTimeoutIds = [];

const clearBurstTimeouts = () => {
  burstTimeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
  burstTimeoutIds = [];
};

const scheduleTabsIndicatorFix = ({ burst = false } = {}) => {
  if (frameId) cancelAnimationFrame(frameId);
  frameId = requestAnimationFrame(() => {
    fixTabsIndicatorsForAppZoom();
    frameId = requestAnimationFrame(fixTabsIndicatorsForAppZoom);
  });

  if (!burst) return;

  clearBurstTimeouts();
  [50, 150, 350, 700].forEach((delayMs) => {
    burstTimeoutIds.push(
      setTimeout(() => {
        fixTabsIndicatorsForAppZoom();
      }, delayMs)
    );
  });

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    document.fonts.ready.then(() => {
      fixTabsIndicatorsForAppZoom();
      requestAnimationFrame(fixTabsIndicatorsForAppZoom);
    });
  }
};

export const scheduleTabsIndicatorZoomFix = (options) => {
  scheduleTabsIndicatorFix(options);
};

const getScrollableScrollerFromButtonEvent = (event) => {
  const scrollButton = event.target?.closest?.(SCROLL_BUTTON_SELECTOR);
  if (!scrollButton) return null;

  const tabsRoot = scrollButton.closest('.MuiTabs-root');
  const scroller = tabsRoot?.querySelector('.MuiTabs-scroller');
  if (!scroller || !shouldSkipTabsZoomFix(tabsRoot)) return null;

  return {
    scroller,
    intentDirection: getScrollButtonIntentDirection(scrollButton, tabsRoot),
  };
};

export const subscribeTabsIndicatorZoomFix = () => {
  const handleZoomOrResize = (event) => {
    scheduleTabsIndicatorFix({ burst: event?.type === 'app_zoom_update' });
  };

  const handleScroll = (event) => {
    if (!event.target?.classList?.contains('MuiTabs-scroller')) return;

    const tabsRoot = event.target.closest('.MuiTabs-root');
    if (shouldSkipTabsZoomFix(tabsRoot)) return;

    scheduleTabsIndicatorFix();
  };

  const handleScrollButtonPress = (event) => {
    const target = getScrollableScrollerFromButtonEvent(event);
    if (!target) return;
    activateScrollerExplore(target.scroller, target.intentDirection);
  };

  const handleScrollerScroll = (event) => {
    if (!event.target?.classList?.contains('MuiTabs-scroller')) return;
    updateScrollerExplorePosition(event.target);
  };

  const handleTabSelect = (event) => {
    const tab = event.target?.closest?.('.MuiTab-root');
    if (!tab) return;

    const scroller = tab.closest('.MuiTabs-root')?.querySelector('.MuiTabs-scroller');
    if (scroller) deactivateScrollerExplore(scroller);
  };

  const handleMutation = (mutations) => {
    const shouldFix = mutations.some((mutation) => {
      const target = mutation.target;
      if (!(target instanceof Element)) return false;

      if (mutation.type === 'childList') {
        return mutation.addedNodes.length > 0
          && [...mutation.addedNodes].some(
            (node) => node instanceof Element
              && (node.matches?.('.MuiTabs-root') || node.querySelector?.('.MuiTabs-root')
                || node.matches?.('.MuiDialog-root') || node.querySelector?.('.MuiDialog-root'))
          );
      }

      return target.classList?.contains('MuiTabs-indicator')
        || target.classList?.contains('MuiTab-root')
        || target.classList?.contains('MuiTabs-root')
        || target.classList?.contains('MuiTabs-flexContainer');
    });

    if (shouldFix) {
      scheduleTabsIndicatorFix({ burst: true });
    }
  };

  const mutationObserver = new MutationObserver((mutations) => {
    handleMutation(mutations);
    mutations.forEach((mutation) => {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'style') return;
      const target = mutation.target;
      if (!(target instanceof Element) || !target.classList?.contains('MuiTabs-indicator')) return;

      const tabsRoot = target.closest('.MuiTabs-root');
      if (shouldSkipTabsZoomFix(tabsRoot)) return;

      setTimeout(() => scheduleTabsIndicatorFix(), 0);
    });
  });

  mutationObserver.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
    childList: true,
  });

  window.addEventListener('app_zoom_update', handleZoomOrResize);
  window.addEventListener('resize', handleZoomOrResize);
  document.addEventListener('scroll', handleScroll, true);
  document.addEventListener('scroll', handleScrollerScroll, true);
  document.addEventListener('pointerdown', handleScrollButtonPress, true);
  document.addEventListener('click', handleTabSelect, true);
  scheduleTabsIndicatorFix({ burst: true });

  return () => {
    if (frameId) cancelAnimationFrame(frameId);
    clearBurstTimeouts();
    mutationObserver.disconnect();
    resizeObservers.forEach((observer) => observer.disconnect());
    resizeObservers.clear();
    observedTabsRoots.clear();
    guardedScrollers.forEach((scroller) => {
      const guard = scrollerGuards.get(scroller);
      if (guard?.rafId) cancelAnimationFrame(guard.rafId);
    });
    guardedScrollers.clear();
    window.removeEventListener('app_zoom_update', handleZoomOrResize);
    window.removeEventListener('resize', handleZoomOrResize);
    document.removeEventListener('scroll', handleScroll, true);
    document.removeEventListener('scroll', handleScrollerScroll, true);
    document.removeEventListener('pointerdown', handleScrollButtonPress, true);
    document.removeEventListener('click', handleTabSelect, true);
  };
};
